import BreadcrumbItem from 'Components/Atoms/BreadcrumbItem/BreadcrumbItem';
import Container from 'Components/Atoms/Container/Container';
import Grid from 'Components/Atoms/Grid/Grid';
import PageLoader from 'Components/Atoms/Loader/PageLoader/PageLoader';
import Title from 'Components/Atoms/Title/Title';
import Breadcrumbs from 'Components/Molecules/Breadcrumbs/Breadcrumbs';
import DetailedHeader from 'Components/Molecules/Detailed/DetailedHeader/DetailedHeader';
import DisplayName from 'Components/Molecules/DisplayName/DisplayName';
import PageHeader from 'Components/Organisms/PageHeader/PageHeader';
import LayoutSidebar from 'Components/Molecules/LayoutSidebar/LayoutSidebar';
import LayoutWrapper from 'Components/Molecules/LayoutWrapper/LayoutWrapper';
import ModulesTab from 'Components/Molecules/ModulesTab/ModulesTab';
import EntityRelationSidebar from 'Components/Organisms/Entity/EntityRelationSidebar/EntityRelationSidebar';
import LayoutContainer from 'Components/Organisms/LayoutContainer/LayoutContainer';
import LayoutSystem from 'Components/Organisms/LayoutSystem/LayoutSystem';
import React, {useEffect, useMemo} from 'react';
import {connect} from 'react-redux';
import {ROUTES} from 'Services/Route/Route.types';
import EntityHelper from 'Services/Settings/Entity/EntityHelper';
import {Tab, TabConsumer, TabProvider} from 'Services/Tab/TabContext';
import {fetchBusinessUnitV2, fetchBusinessUnitValues} from 'Store/BusinessUnit/Actions';
import {fetchCandidateV2, fetchCandidateValues} from 'Store/Candidate/Actions';
import {fetchContactV2, fetchContactValues} from 'Store/Contact/Actions';
import {StoreState} from 'Store/Reducers';
import {getEntityByName} from 'Store/Settings/Entities/Selectors';
import {getLayoutsByEntityTypeIdAsObject} from 'Store/Settings/Layout/Selectors';
import {getRecordsByEntityName, isEntityFetching} from 'Store/Shared/Store/Values/Selector';
import {fetchVacancyV2, fetchVacancyValues} from 'Store/Vacancy/Actions';

import Dropzone from 'Components/Organisms/Files/Dropzone';
import {fetchOffer, fetchOfferValues} from 'Store/Offer/Actions';

const DetailedTemplate = ({
    children, sidebar,
    entity_name, record_id,

    layoutsByEntityType,
    isEntityFetching, entity,
    recordById,

    ...props
}: DetailedTemplateProps) => {
    const fetchRecord = useMemo(() => EntityHelper.fetchRecordById(props, entity_name), [entity_name]);
    
    const record = useMemo(() => {
        if (!record_id) return {};

        return recordById?.[record_id];
    }, [recordById, record_id]);

    const layouts = useMemo(() => {
        if (!layoutsByEntityType || !record) return [];
        
        return layoutsByEntityType?.[record?.type_id] ?? [];
    }, [record, layoutsByEntityType]);

    useEffect(() => {
        fetchRecord(record_id);
    }, []);

    if (isEntityFetching) return <PageLoader description={`${EntityHelper.getSingularDisplayValueByName(entity_name)} laden...`} />; 

    if (!layouts) return <Grid
        padding='medium'
        axis='horizontal'
        position='centered'
    >
        <Title>
            Layouts voor {EntityHelper.getDisplayValueByName(entity_name)} niet geconfigureerd.
        </Title>
    </Grid>;

    if (!record)  return <Grid
        padding='medium'
        axis='horizontal'
        position='centered'
    >
        <Title>
            { EntityHelper.getSingularDisplayValueByName(entity_name) } met id: "<strong>{ record_id }</strong>" niet gevonden.
        </Title>
    </Grid>;

    return <TabProvider>
        <TabConsumer>
            {
                ({ setTab }: any) => <>
                    {
                        !!entity?.modules?.files
                        && <Dropzone
                            id={record_id}
                            onDragStart={() => setTab(Tab.FILES)}
                        />
                    }
                </>
            }
        </TabConsumer>

        <Grid>
            <PageHeader
                breadcrumbs={
                    <Breadcrumbs>
                        <BreadcrumbItem size='small' path={ROUTES.DASHBOARD.path}>Dashboard</BreadcrumbItem>
                        <BreadcrumbItem size='small' path={ROUTES?.[EntityHelper.entityEndpoint(entity_name)?.toUpperCase()]?.path}>{ EntityHelper.getDisplayValueByName(entity_name) }</BreadcrumbItem>
                        <BreadcrumbItem size='small'>
                            <DisplayName
                                entity={entity}
                                entity_id={record_id}
                            />
                        </BreadcrumbItem>
                    </Breadcrumbs>
                }
                padding='bottomless'
            >
                {
                    !isEntityFetching
                    && record
                    && <DetailedHeader
                        entity_id={entity.id}
                        record_id={record_id}
                    />
                }
            </PageHeader>

            <TabConsumer>
                {
                    ({ state: { active } }) => <Container padding='vertical'>
                        { active === Tab.OVERVIEW
                        && <Grid
                            axis='horizontal'
                            align='start'
                        >
                            <LayoutSidebar>
                                <LayoutSystem
                                    entity_id={entity.id}
                                    record_id={record_id}
                                />

                                <EntityRelationSidebar
                                    id={record_id}
                                    entity_id={entity.id}
                                    entity={entity}
                                />

                                {
                                    record
                                    && sidebar
                                }
                            </LayoutSidebar>
                            <LayoutWrapper>
                                {
                                    layouts
                                    ?.filter(i => i.name !== entity_name)
                                    ?.map(i =>
                                        <LayoutContainer
                                            entity_id={entity.id}
                                            record_id={record_id}
                                            layout={i}
                                        />
                                    )
                                }
                                { children }
                            </LayoutWrapper>
                        </Grid> }
                        <ModulesTab
                            activeTab={active}
                            id={record_id}
                            entity_id={entity.id}
                        />
                    </Container>
                }
            </TabConsumer>
        </Grid>
    </TabProvider>;
}

interface Props {
    sidebar?: any
    children?: any

    entity_name: string
    record_id: string
}

const mapState = (state: StoreState, props: Props) => ({
    entity: getEntityByName(state, props.entity_name),
    layoutsByEntityType: getLayoutsByEntityTypeIdAsObject(state),
    
    recordById: getRecordsByEntityName(state, props.entity_name),
    isEntityFetching: isEntityFetching(state, props.entity_name, props.record_id)
});

const mapDispatch = {
    fetchContactV2,
    fetchContactValues,
    fetchBusinessUnitV2,
    fetchBusinessUnitValues,
    fetchCandidateV2,
    fetchCandidateValues,
    fetchVacancyV2,
    fetchVacancyValues,
    fetchOffer,
    fetchOfferValues,
};

type DetailedTemplateProps = Props & ReturnType<typeof mapState> & typeof mapDispatch;

export default connect(mapState, mapDispatch)(DetailedTemplate);